import React, { useState, useEffect } from 'react'
import { controlCenterApi, type ReportsSummaryData } from '../../services/controlCenterApi'

function StatCard({ label, value, color = 'text-white', sub }: { label: string; value: number | string; color?: string; sub?: string }): React.ReactElement {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</p>
      <p className={`text-2xl font-bold font-mono ${color}`}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
    </div>
  )
}

export function ReportsDashboard(): React.ReactElement {
  const [data, setData] = useState<ReportsSummaryData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetch = async () => {
      try {
        const report = await controlCenterApi.getReportsSummary()
        setData(report)
        setError('')
      } catch {
        setError('Failed to load report data')
      }
      setIsLoading(false)
    }
    fetch()
  }, [])

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-400 text-sm mt-3">Loading reports...</span>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-lg">
        <p className="text-red-300 text-sm">{error}</p>
      </div>
    )
  }

  if (!data) return <div />

  const { accounts, users, staff } = data

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white tracking-tight">Reports</h1>
        <p className="text-slate-500 text-xs mt-1 font-mono">
          Generated {new Date(data.generated_at).toLocaleString()}
        </p>
      </div>

      {/* Accounts Section */}
      <section className="mb-8">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Accounts</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <StatCard label="Active Accounts" value={accounts.total_active} color="text-emerald-400" />
          <StatCard label="New This Month" value={accounts.new_this_month} color={accounts.new_this_month > 0 ? 'text-primary' : 'text-slate-400'} />
          <StatCard label="Groups" value={accounts.groups} />
          <StatCard label="Rooftops" value={accounts.rooftops} />
          <StatCard label="Inactive" value={accounts.total_inactive} color={accounts.total_inactive > 0 ? 'text-amber-400' : 'text-slate-400'} />
          <StatCard label="Deleted" value={accounts.total_deleted} color={accounts.total_deleted > 0 ? 'text-red-400' : 'text-slate-400'} />
          <StatCard label="On Billing Hold" value={accounts.on_billing_hold} color={accounts.on_billing_hold > 0 ? 'text-amber-400' : 'text-slate-400'} />
        </div>
      </section>

      {/* Users Section */}
      <section className="mb-8">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Users</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatCard label="Total Active Users" value={users.total_active} color="text-emerald-400" />
          <StatCard label="New This Month" value={users.new_this_month} color={users.new_this_month > 0 ? 'text-primary' : 'text-slate-400'} />
          <StatCard label="Signed In This Month" value={users.signed_in_this_month} color={users.signed_in_this_month > 0 ? 'text-primary' : 'text-slate-400'} />
        </div>
      </section>

      {/* Staff Section */}
      <section className="mb-8">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Staff</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatCard label="Total Staff" value={staff.total} />
        </div>
      </section>

      {/* Coming soon notice */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
        <svg className="w-8 h-8 text-slate-600 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
        </svg>
        <p className="text-slate-500 text-sm">
          Additional reports (inventory analytics, lead metrics, date-range filtering, CSV export) coming in Phase 8.
        </p>
      </div>
    </div>
  )
}
